import { getMultiAgentAuthHeaders, multiAgentBaseUrl } from "./client";
import type { StreamHandle, StreamHandlers } from "./types";

const BASE = multiAgentBaseUrl;

const TERMINAL_EVENT_TYPES = new Set([
  "run_completed", "run_failed", "run_cancelled", "budget_exceeded", "completed_with_no_evidence", "done",
]);

// Per-session SSE tickets, captured at create/refine time (dev-agent parity).
// Keyed by runId so the stream can authenticate with `?ticket=` instead of
// putting the bearer on the SSE URL. Falls back to header auth when absent
// (e.g. hydrating a historical run).
const runTickets = new Map<string, string>();

export function registerRunTicket(runId: string, ticket?: string | null): void {
  if (runId && ticket) runTickets.set(runId, ticket);
}

export function clearRunTicket(runId: string): void {
  runTickets.delete(runId);
}

/**
 * Subscribe to a run's SSE stream with auto-reconnect + Last-Event-ID resume.
 */
export function subscribeToRun(
  runId: string,
  handlers: StreamHandlers,
  lastEventId?: number,
  sessionId?: string | null,
): StreamHandle {
  const controller = new AbortController();
  const authHeaders = getMultiAgentAuthHeaders();
  if (sessionId) authHeaders["X-Session-ID"] = sessionId;
  let currentLastId = lastEventId ?? 0;
  let attempt = 0;
  let stalled = false;
  let stalledTimer: ReturnType<typeof setTimeout> | null = null;
  let terminated = false;
  let attemptCtrl: AbortController | null = null;
  let triedHeaderAuth = false;
  const MAX_RECONNECT_ATTEMPTS = 10;

  const setStatus = (s: "connecting" | "connected" | "disconnected" | "reconnecting") => {
    handlers.onStatus?.(s);
  };

  const resetStalled = () => {
    if (stalledTimer) clearTimeout(stalledTimer);
    stalled = false;
    stalledTimer = setTimeout(() => {
      stalled = true;
      try { attemptCtrl?.abort(); } catch { /* noop */ }
    }, 60_000);
  };

  const backoffMs = () => Math.min(8000, 250 * Math.pow(2, attempt - 1));

  (async () => {
    while (!controller.signal.aborted && !terminated) {
      let outerAbort: (() => void) | null = null;
      try {
        setStatus(attempt === 0 ? "connecting" : "reconnecting");
        const headers: Record<string, string> = {
          Accept: "text/event-stream",
          ...authHeaders,
        };
        if (currentLastId > 0) {
          headers["Last-Event-ID"] = String(currentLastId);
        }
        attemptCtrl = new AbortController();
        outerAbort = () => attemptCtrl?.abort();
        controller.signal.addEventListener("abort", outerAbort);
        const ticket = runTickets.get(runId);
        const streamUrl = ticket
          ? `${BASE}/api/runs/${encodeURIComponent(runId)}/stream?ticket=${encodeURIComponent(ticket)}`
          : `${BASE}/api/runs/${encodeURIComponent(runId)}/stream`;
        const res = await fetch(streamUrl, {
          method: "GET",
          headers,
          signal: attemptCtrl.signal,
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            // A stale/expired one-time ticket 401s. The backend stream ALSO
            // accepts bearer auth (authHeaders are always attached), so drop the
            // ticket and retry ONCE on header auth instead of giving up — this
            // is what made a transient ticket failure show a hard "Failed".
            if (runTickets.has(runId) && !triedHeaderAuth) {
              triedHeaderAuth = true;
              runTickets.delete(runId);
              setStatus("reconnecting");
              continue; // immediate retry; streamUrl now uses bearer-only auth
            }
            handlers.onError?.(new Error(`stream HTTP ${res.status}`));
            setStatus("disconnected");
            return;
          }
          throw new Error(`stream HTTP ${res.status}`);
        }
        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error("no readable body");
        }
        setStatus("connected");
        attempt = 0;
        resetStalled();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          resetStalled();

          let idx: number;
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const dataLine = frame.split("\n").find((l) => l.startsWith("data: "));
            if (!dataLine) continue;
            try {
              const event = JSON.parse(dataLine.slice(6));
              if (typeof event._event_id === "number" && event._event_id > currentLastId) {
                currentLastId = event._event_id;
              }
              handlers.onEvent(event);
              const et = event.event_type ?? event.event;
              if (TERMINAL_EVENT_TYPES.has(et as string)) {
                terminated = true;
                runTickets.delete(runId); // run is over — free its one-time ticket
                handlers.onClose?.();
                setStatus("disconnected");
                if (stalledTimer) clearTimeout(stalledTimer);
                return;
              }
            } catch (parseErr) {
              console.warn("[multi-agent SSE] parse error", parseErr);
            }
          }
        }
      } catch (err) {
        if (controller.signal.aborted) {
          if (stalledTimer) clearTimeout(stalledTimer);
          return;
        }
        if ((err as Error).name !== "AbortError") {
          console.warn("[multi-agent SSE] error, will reconnect:", (err as Error).message);
        }
        if (stalled) {
          console.warn("[multi-agent SSE] stale (no events for 60s) — reconnecting");
          stalled = false;
        }
      } finally {
        if (outerAbort) {
          controller.signal.removeEventListener("abort", outerAbort);
        }
        attemptCtrl = null;
      }

      if (terminated || controller.signal.aborted) {
        if (stalledTimer) clearTimeout(stalledTimer);
        return;
      }
      attemptCtrl = null;
      setStatus("reconnecting");
      attempt += 1;
      // Bound the reconnect loop (dev-agent parity: max 10 attempts) so a truly
      // dead stream surfaces a failure instead of retrying forever.
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        handlers.onError?.(new Error("reconnect exhausted"));
        if (stalledTimer) clearTimeout(stalledTimer);
        setStatus("disconnected");
        return;
      }
      await new Promise((r) => setTimeout(r, backoffMs()));
    }

    if (stalledTimer) clearTimeout(stalledTimer);
    setStatus("disconnected");
  })();

  return { abort: () => controller.abort() };
}
